/** The Empress's sounds (04 §5, 03 §5): her warning, her wounds and her end, and the lance. Pure. */

import { LEAD_IN, stepSeconds } from '../music/clock';
import { EMPRESS } from '../music/empress';
import { midiToHz } from '../notes';
import { intBelow, type Rand, vary } from '../rand';
import { adsr, type VoiceSpec, type Wave } from '../voice';
import { hit, type Sfx, tone } from './kit';

/**
 * WARNING: a two-tone alarm, B5 and G5 in turn for two and a half seconds, doubled an octave
 * below. Her theme starts on the same event, so the alarm keeps its time - the two tones are
 * eighth notes of it, and the fifth and third of the E minor it opens on.
 */
export const warning: Sfx = () => {
  const eighth = 2 * stepSeconds(EMPRESS.bpm);
  const alarm = (wave: Wave, octave: number, level: number): VoiceSpec => ({
    ...tone(wave, midiToHz(83 + octave), 14 * eighth - 0.12, adsr(0.01, 0.1, 0.85, 0.12), level),
    at: LEAD_IN,
    width: 0.35,
    steps: [midiToHz(83 + octave), midiToHz(79 + octave)],
    stepTime: eighth,
  });
  return [alarm('pulse', 0, 0.18), alarm('triangle', -12, 0.14)];
};

/**
 * A shot lands on her: a thud, one voice dropping an octave and a half in a tenth of a second -
 * low and short enough to hear a hundred of (a spread volley from a dual fighter lands six). A
 * muffled square rather than a triangle, so small speakers that have no bass still give a knock.
 */
export const empressHit: Sfx = (r) => [
  {
    ...hit('pulse', vary(r, 196, 0.08), 0.1, 0.3),
    width: 0.5,
    to: 65,
    glide: 0.08,
    filter: { type: 'lowpass', freq: 900, to: 300, sweep: 0.08, q: 1 },
  },
];

/** One voice of the roar: a sawtooth swelling and climbing, shaken hard, its filter opening. */
function growl(hz: number, level: number): VoiceSpec {
  return {
    ...tone('sawtooth', hz, 1.1, adsr(0.12, 0.3, 0.8, 0.4), level),
    to: hz * 1.7,
    glide: 0.9,
    vibrato: { rate: 27, depth: 90, delay: 0 },
    filter: { type: 'lowpass', freq: 500, to: 2600, sweep: 0.8, q: 5 },
  };
}

/** Half her hit points gone and she turns: a roar, two growls a fifth apart with breath over them. */
export const empressPhase: Sfx = (r) => {
  const hz = vary(r, 62, 0.05);
  return [
    growl(hz, 0.3),
    growl(hz * 1.51, 0.16),
    {
      ...tone('noise', 900, 1.1, adsr(0.15, 0.3, 0.7, 0.4), 0.3),
      to: 2400,
      glide: 0.9,
      filter: { type: 'bandpass', freq: 500, to: 1600, sweep: 0.9, q: 1.5 },
    },
  ];
};

/** One of her dying explosions: a boom, no two alike. */
export const empressBlast: Sfx = (r) => [
  {
    ...hit('noise', vary(r, 3200, 0.25), 0.4, 0.5),
    to: 300,
    glide: 0.35,
    filter: { type: 'lowpass', freq: 4500, to: 300, sweep: 0.35, q: 3 },
  },
  { ...hit('triangle', vary(r, 130, 0.2), 0.25, 0.4), to: 36, glide: 0.22 },
];

/** Sixteen notes tumbling down three and a half octaves, each a few semitones off true: the wreck. */
function crumble(r: Rand): VoiceSpec {
  const steps = Array.from({ length: 16 }, (_, i) => midiToHz(84 - i * 3 + intBelow(r, 5)));
  return {
    ...tone('pulse', steps[0] ?? 1000, 1.6, adsr(0.005, 0.8, 0.3, 0.3), 0.1),
    at: 0.1,
    width: 0.125,
    steps,
    stepTime: 0.1,
    once: true,
  };
}

/** She is destroyed: a long collapse - two and a half seconds of noise closing down, a howl falling four octaves. */
export const empressKilled: Sfx = (r) => [
  {
    ...hit('noise', 5200, 2.5, 0.75),
    to: 160,
    glide: 2.2,
    filter: { type: 'lowpass', freq: 8000, to: 120, sweep: 2.2, q: 5 },
  },
  {
    ...tone('sawtooth', vary(r, 392, 0.03), 2.2, adsr(0.01, 1.2, 0.4, 0.3), 0.26),
    to: 31,
    glide: 2.2,
    vibrato: { rate: 19, depth: 80, delay: 0 },
    filter: { type: 'lowpass', freq: 2600, to: 240, q: 4 },
  },
  { ...hit('triangle', 100, 0.6, 0.55), to: 26, glide: 0.5 },
  crumble(r),
];

/** The lance's warning line (50 ticks): a charging whine, swelling as it climbs three octaves. */
export const lanceWarn: Sfx = () => [
  {
    ...tone('sawtooth', 220, 0.8, adsr(0.5, 0.1, 1, 0.04), 0.2),
    to: 1760,
    glide: 0.8,
    vibrato: { rate: 31, depth: 45, delay: 0 },
    filter: { type: 'lowpass', freq: 1200, to: 6000, sweep: 0.8, q: 6 },
  },
  { ...tone('pulse', 331, 0.8, adsr(0.6, 0.1, 1, 0.04), 0.11), width: 0.15, to: 2640, glide: 0.8 },
];

/** The lance fires: a crack and a sawtooth dropping three octaves. The burn itself is ambience. */
export const lanceFire: Sfx = (r) => [
  { ...hit('noise', 16000, 0.12, 0.55), filter: { type: 'highpass', freq: 2200, q: 0 } },
  { ...hit('sawtooth', vary(r, 1568, 0.03), 0.18, 0.3), to: 196, glide: 0.16 },
];

/** The lance goes out: a fizzle falling away. */
export const lanceEnd: Sfx = () => [
  { ...hit('noise', 7000, 0.22, 0.26), to: 900, glide: 0.2, filter: { type: 'highpass', freq: 1200, q: 0 } },
  { ...hit('sawtooth', 392, 0.2, 0.14), to: 65, glide: 0.2, filter: { type: 'lowpass', freq: 2000, q: 2 } },
];
